class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    overlay.removeEventListener('click', this.boundClose);
    overlay.addEventListener('click', this.boundClose);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerAccessibilityBound === 'true') return;

    cartLink.dataset.cartDrawerAccessibilityBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const openDrawer = options.openDrawer !== false;
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();

      if (openDrawer) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const section = parsedDocument.querySelector(selector);

    return section ? section.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

class EcomhardGiftController {
  constructor() {
    this.syncing = false;
    this.scheduled = false;
    this.timer = null;
    this.observer = null;
    this.releaseTimer = null;
    this.pendingCartRequests = 0;
    this.lastCartMutationAt = 0;
    this.checkoutInProgress = false;

    this.boot = this.boot.bind(this);
    this.schedule = this.schedule.bind(this);
    this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    this.handleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.boot, { once: true });
    } else {
      this.boot();
    }
  }

  boot() {
    if (!this.isGiftFeatureActive()) return;

    this.patchFetch();
    this.bindCartEvents();
    this.bindCartInteractionGuard();
    this.bindCheckoutGuard();
    this.observeCartChanges();
    this.schedule(80);
  }

  isGiftFeatureActive() {
    return Boolean(this.getConfig());
  }

  patchFetch() {
    if (window.__ecomhardGiftFetchPatched || typeof window.fetch !== 'function') return;

    const originalFetch = window.fetch.bind(window);
    const controller = this;

    window.__ecomhardGiftFetchPatched = true;
    window.__ecomhardGiftOriginalFetch = originalFetch;

    window.fetch = function ecomhardGiftFetch(resource, init) {
      if (!controller.isGiftFeatureActive()) {
        return originalFetch(resource, init);
      }

      const url = controller.getFetchUrl(resource);
      const isCartMutation = controller.isCartMutationUrl(url);

      if (isCartMutation) {
        controller.pendingCartRequests += 1;
        controller.lastCartMutationAt = Date.now();
        controller.setCheckoutButtonsLoading(true, 'Atualizando carrinho...');
      }

      const request = originalFetch(resource, init);

      if (!isCartMutation || !request || typeof request.finally !== 'function') {
        return request;
      }

      return request.finally(() => {
        controller.pendingCartRequests = Math.max(0, controller.pendingCartRequests - 1);
        controller.lastCartMutationAt = Date.now();
        controller.schedule(90);
        controller.releaseCheckoutWhenSettled();
      });
    };
  }

  getFetchUrl(resource) {
    if (!resource) return '';
    if (typeof resource === 'string') return resource;
    if (resource instanceof URL) return resource.href;
    if (typeof Request !== 'undefined' && resource instanceof Request) return resource.url;
    if (typeof resource.url === 'string') return resource.url;

    return '';
  }

  normalizeCartPath(path) {
    return String(path || '')
      .replace(/\.js$/, '')
      .replace(/\/$/, '');
  }

  isCartMutationUrl(url) {
    if (!url) return false;

    let pathname = '';

    try {
      pathname = new URL(url, window.location.origin).pathname;
    } catch (error) {
      pathname = String(url).split('?')[0];
    }

    const currentPath = this.normalizeCartPath(pathname);
    const mutationPaths = [
      routes?.cart_add_url,
      routes?.cart_change_url,
      routes?.cart_update_url,
      '/cart/add',
      '/cart/change',
      '/cart/update',
    ]
      .filter(Boolean)
      .map((path) => this.normalizeCartPath(path));

    return mutationPaths.some((path) => currentPath === path || currentPath.endsWith(path));
  }

  bindCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (!this.isGiftFeatureActive()) return;

      this.markCartChanging();
      this.schedule(140);
    });
  }

  bindCartInteractionGuard() {
    if (this.cartInteractionGuardBound) return;
    this.cartInteractionGuardBound = true;

    document.addEventListener(
      'click',
      (event) => {
        if (!this.isGiftFeatureActive()) return;

        const cartAction = event.target.closest(
          'cart-remove-button button, cart-remove-button a, quantity-input button, .quantity__button, [href*="/cart/change"], [data-index][type="button"]'
        );

        if (!cartAction) return;
        if (!cartAction.closest('cart-drawer, cart-items, form[action*="/cart"]')) return;

        this.markCartChanging();
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        if (!this.isGiftFeatureActive()) return;

        const target = event.target;
        if (!target || !target.matches('input[name^="updates"], input.quantity__input, quantity-input input')) return;
        if (!target.closest('cart-drawer, cart-items, form[action*="/cart"]')) return;

        this.markCartChanging();
      },
      true
    );
  }

  bindCheckoutGuard() {
    if (this.checkoutGuardBound) return;
    this.checkoutGuardBound = true;

    document.addEventListener('click', this.handleCheckoutClick, true);
    document.addEventListener('submit', this.handleCheckoutSubmit, true);
  }

  handleCheckoutClick(event) {
    if (!this.isGiftFeatureActive()) return;

    const button = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!button) return;

    const form = this.getCheckoutForm(button);
    if (this.isCheckoutUnlocked(form, button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutAfterValidation(button, form);
  }

  handleCheckoutSubmit(event) {
    if (!this.isGiftFeatureActive()) return;

    const form = event.target;
    const submitter = event.submitter;
    const isCheckoutSubmit = submitter?.matches?.('button[name="checkout"], input[name="checkout"]');

    if (!isCheckoutSubmit) return;
    if (this.isCheckoutUnlocked(form, submitter)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutAfterValidation(submitter, form);
  }

  getCheckoutForm(button) {
    if (!button) return null;

    const formId = button.getAttribute('form');
    if (formId) return document.getElementById(formId);

    return button.closest('form');
  }

  isCheckoutUnlocked(form, button) {
    return form?.dataset.ecomhardGiftCheckoutUnlocked === 'true' || button?.dataset.ecomhardGiftCheckoutUnlocked === 'true';
  }

  async checkoutAfterValidation(button, form) {
    if (!this.isGiftFeatureActive()) {
      this.unlockAndSubmitCheckout(button, form);
      return;
    }

    if (this.checkoutInProgress) return;

    this.checkoutInProgress = true;
    this.setCheckoutButtonsLoading(true, 'Conferindo brinde...');
    this.clearCheckoutError();

    try {
      await this.waitForPendingCartRequests();
      await this.waitForCurrentSync();

      const result = await this.ensureGiftState({ render: true });

      if (!result.valid) {
        this.showCheckoutError('Atualizamos seu carrinho. Clique em finalizar compra novamente.');
        this.checkoutInProgress = false;
        this.setCheckoutButtonsLoading(false);
        return;
      }

      await this.waitForPendingCartRequests();
      this.unlockAndSubmitCheckout(button, form);
    } catch (error) {
      console.error('[ECOMHARD] Não foi possível validar o brinde antes do checkout.', error);
      this.showCheckoutError('Não foi possível validar o brinde agora. Tente finalizar a compra novamente.');
      this.checkoutInProgress = false;
      this.setCheckoutButtonsLoading(false);
    }
  }

  unlockAndSubmitCheckout(button, form) {
    if (!form) {
      window.location.href = '/checkout';
      return;
    }

    form.dataset.ecomhardGiftCheckoutUnlocked = 'true';
    if (button) button.dataset.ecomhardGiftCheckoutUnlocked = 'true';

    /*
      O botão é desabilitado durante a validação para bloquear o clique rápido.
      Antes de enviar o formulário, precisamos restaurar o botão e enviar o campo
      hidden checkout manualmente. Assim o Shopify entende que é checkout e não
      apenas atualização da página /cart.
    */
    this.setCheckoutButtonsLoading(false);
    this.checkoutInProgress = false;

    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }

    let checkoutInput = form.querySelector('input[type="hidden"][name="checkout"]');
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      form.appendChild(checkoutInput);
    }
    checkoutInput.value = button?.value || 'Checkout';

    window.setTimeout(() => {
      delete form.dataset.ecomhardGiftCheckoutUnlocked;
      if (button) delete button.dataset.ecomhardGiftCheckoutUnlocked;
    }, 3000);

    form.submit();
  }

  markCartChanging() {
    if (!this.isGiftFeatureActive()) return;

    this.lastCartMutationAt = Date.now();
    this.setCheckoutButtonsLoading(true, 'Atualizando carrinho...');
    this.releaseCheckoutWhenSettled();
  }

  releaseCheckoutWhenSettled() {
    window.clearTimeout(this.releaseTimer);

    if (!this.isGiftFeatureActive()) {
      this.setCheckoutButtonsLoading(false);
      return;
    }

    this.releaseTimer = window.setTimeout(async () => {
      if (this.checkoutInProgress || this.pendingCartRequests > 0 || this.syncing) {
        this.releaseCheckoutWhenSettled();
        return;
      }

      if (Date.now() - this.lastCartMutationAt < 300) {
        this.releaseCheckoutWhenSettled();
        return;
      }

      try {
        await this.ensureGiftState({ render: true });
      } catch (error) {
        console.error('[ECOMHARD] Não foi possível finalizar a sincronização do brinde.', error);
      }

      if (!this.checkoutInProgress && this.pendingCartRequests === 0 && !this.syncing) {
        this.setCheckoutButtonsLoading(false);
      }
    }, 160);
  }

  setCheckoutButtonsLoading(isLoading, text = 'Atualizando carrinho...') {
    if (isLoading && !this.isGiftFeatureActive()) return;

    const buttons = document.querySelectorAll('button[name="checkout"], input[name="checkout"]');

    buttons.forEach((button) => {
      if (isLoading) {
        if (!button.dataset.ecomhardGiftOriginalDisabled) {
          button.dataset.ecomhardGiftOriginalDisabled = button.disabled ? 'true' : 'false';
        }

        if (button.tagName === 'INPUT') {
          if (!button.dataset.ecomhardGiftOriginalText) button.dataset.ecomhardGiftOriginalText = button.value || '';
          button.value = text;
        } else {
          if (!button.dataset.ecomhardGiftOriginalText) button.dataset.ecomhardGiftOriginalText = button.textContent.trim();
          button.textContent = text;
        }

        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('ecomhard-checkout-is-syncing');
        return;
      }

      if (button.dataset.ecomhardGiftOriginalText) {
        if (button.tagName === 'INPUT') {
          button.value = button.dataset.ecomhardGiftOriginalText;
        } else {
          button.textContent = button.dataset.ecomhardGiftOriginalText;
        }
      }

      if (button.dataset.ecomhardGiftOriginalDisabled !== 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }

      button.classList.remove('ecomhard-checkout-is-syncing');
      delete button.dataset.ecomhardGiftOriginalDisabled;
      delete button.dataset.ecomhardGiftOriginalText;
    });
  }

  clearCheckoutError() {
    const errorTargets = [
      document.getElementById('CartDrawer-CartErrors'),
      document.getElementById('cart-errors'),
      document.querySelector('[data-cart-errors]'),
    ].filter(Boolean);

    errorTargets.forEach((target) => {
      target.textContent = '';
    });
  }

  showCheckoutError(message) {
    const errorTarget =
      document.getElementById('CartDrawer-CartErrors') ||
      document.getElementById('cart-errors') ||
      document.querySelector('[data-cart-errors]');

    if (errorTarget) {
      errorTarget.textContent = message;
      return;
    }

    window.alert(message);
  }

  wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async waitForPendingCartRequests(timeout = 4500) {
    const startedAt = Date.now();

    while (this.pendingCartRequests > 0 || Date.now() - this.lastCartMutationAt < 220) {
      if (Date.now() - startedAt > timeout) break;
      await this.wait(80);
    }
  }

  async waitForCurrentSync(timeout = 4500) {
    const startedAt = Date.now();

    while (this.syncing) {
      if (Date.now() - startedAt > timeout) break;
      await this.wait(80);
    }
  }

  observeCartChanges() {
    if (this.observer) return;

    const targets = [
      document.querySelector('cart-drawer'),
      document.querySelector('cart-items'),
    ].filter(Boolean);

    if (!targets.length || typeof MutationObserver === 'undefined') return;

    this.observer = new MutationObserver(() => {
      this.schedule(180);
    });

    targets.forEach((target) => {
      this.observer.observe(target, {
        childList: true,
        subtree: true,
      });
    });
  }

  getConfig() {
    const root = document.querySelector('[data-ecomhard-gift-enabled="true"]');
    if (!root) return null;

    const variantId = Number(root.dataset.ecomhardGiftVariantId);
    const threshold = Number(root.dataset.ecomhardGiftThreshold);

    if (!Number.isFinite(variantId) || variantId <= 0 || !Number.isFinite(threshold) || threshold <= 0) {
      return null;
    }

    return { variantId, threshold };
  }

  schedule(delay = 100) {
    if (!this.isGiftFeatureActive()) {
      this.setCheckoutButtonsLoading(false);
      return;
    }

    this.scheduled = true;
    window.clearTimeout(this.timer);

    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.run();
    }, delay);
  }

  async run() {
    if (this.syncing) {
      this.scheduled = true;
      return;
    }

    this.scheduled = false;

    try {
      await this.ensureGiftState({ render: true });
    } catch (error) {
      console.error('[ECOMHARD] Não foi possível sincronizar o brinde automático.', error);
    } finally {
      if (this.scheduled) {
        this.scheduled = false;
        this.schedule(80);
        return;
      }

      if (!this.checkoutInProgress && this.pendingCartRequests === 0) {
        this.setCheckoutButtonsLoading(false);
      }
    }
  }

  async ensureGiftState({ render = true } = {}) {
    if (this.syncing) {
      this.scheduled = true;
      await this.waitForCurrentSync();

      if (this.syncing) return { valid: false };
    }

    this.syncing = true;

    try {
      const config = this.getConfig();
      if (!config) return { valid: true };

      let cart = await this.fetchCart();
      let summary = this.getGiftSummary(cart, config);
      let state = null;

      if (!summary.qualifies && summary.giftLines.length) {
        state = await this.removeGiftLines(summary.giftLines);
      }

      if (summary.qualifies && !this.hasValidGiftLine(summary.giftLines, config.variantId)) {
        if (summary.giftLines.length) {
          state = await this.removeGiftLines(summary.giftLines);
        }

        state = await this.addGift(config.variantId);
      }

      if (state?.sections && render) {
        this.renderCartState(state);
      }

      cart = await this.fetchCart();
      summary = this.getGiftSummary(cart, config);

      if (!summary.qualifies && summary.giftLines.length) {
        state = await this.removeGiftLines(summary.giftLines);

        if (state?.sections && render) {
          this.renderCartState(state);
        }

        cart = await this.fetchCart();
        summary = this.getGiftSummary(cart, config);
      }

      return {
        valid: summary.isSafe,
        qualifies: summary.qualifies,
        eligibleSubtotal: summary.eligibleSubtotal,
        giftLines: summary.giftLines,
      };
    } finally {
      this.syncing = false;
    }
  }

  getGiftSummary(cart, config) {
    const giftLines = cart.items.filter((item) => this.isGiftLine(item));
    const eligibleSubtotal = cart.items.reduce((total, item) => {
      return this.isGiftLine(item) ? total : total + item.final_line_price;
    }, 0);
    const qualifies = eligibleSubtotal >= config.threshold;
    const isSafe = qualifies ? this.hasValidGiftLine(giftLines, config.variantId) : giftLines.length === 0;

    return {
      giftLines,
      eligibleSubtotal,
      qualifies,
      isSafe,
    };
  }

  isGiftLine(item) {
    return String(item?.properties?._ecomhard_brinde || '') === 'true';
  }

  hasValidGiftLine(giftLines, variantId) {
    return (
      giftLines.length === 1 &&
      Number(giftLines[0].variant_id) === Number(variantId) &&
      Number(giftLines[0].quantity) === 1
    );
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  }

  getSectionsToRequest() {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');

    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    if (mainCartItems?.dataset.id) sections.push(mainCartItems.dataset.id);
    if (document.getElementById('main-cart-footer')) sections.push('main-cart-footer');
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');

    return [...new Set(sections)];
  }

  async removeGiftLines(giftLines) {
    const updates = {};

    giftLines.forEach((item) => {
      updates[item.key] = 0;
    });

    return this.requestCartUpdate({ updates });
  }

  async addGift(variantId) {
    const sections = this.getSectionsToRequest();
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _ecomhard_brinde: 'true',
            },
          },
        ],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error(`Gift add request failed with status ${response.status}`);
    }

    return response.json();
  }

  async requestCartUpdate(payload) {
    const sections = this.getSectionsToRequest();
    const response = await fetch(routes.cart_update_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart update request failed with status ${response.status}`);
    }

    return response.json();
  }

  renderCartState(state) {
    if (typeof state.item_count === 'number') {
      const isEmpty = state.item_count === 0;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && state.sections?.['cart-drawer']) {
      drawer.renderContents(state, { openDrawer: false });
    } else {
      this.replaceSectionContent(state, 'cart-icon-bubble', '.shopify-section');
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset.id) {
      this.replaceSectionContent(
        state,
        mainCartItems.dataset.id,
        '.js-contents',
        mainCartItems.querySelector('.js-contents')
      );
    }

    this.replaceSectionContent(
      state,
      'main-cart-footer',
      '.js-contents',
      document.querySelector('#main-cart-footer .js-contents')
    );
  }

  replaceSectionContent(state, sectionId, selector, target = null) {
    const sectionHtml = state.sections?.[sectionId];
    if (!sectionHtml) return;

    const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(selector);
    const element = target || document.getElementById(sectionId);

    if (source && element) {
      element.innerHTML = source.innerHTML;
    }
  }
}

if (!window.EcomhardGiftSync) {
  window.EcomhardGiftSync = new EcomhardGiftController();
}
